import React, { useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle, CreditCard, ShieldCheck } from 'lucide-react';
import { paymentService } from '../services/paymentService';

const CheckoutPage = ({ user, onBackToPlans, onPaymentComplete }) => {
  const selectedPlan = useMemo(() => paymentService.getSelectedPlan(user), [user]);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const payNow = async () => {
    setStatus('loading');
    setMessage('Creating secure payment order...');
    try {
      const order = await paymentService.createPaymentOrder({ user, plan: selectedPlan });
      setMessage('Opening Razorpay Checkout...');
      const paymentResponse = await paymentService.startGatewayPayment({ user, plan: selectedPlan, order });
      setMessage('Verifying payment securely...');
      const updatedUser = await paymentService.verifyPayment(paymentResponse);
      setStatus('success');
      setMessage('Payment completed. Redirecting to dashboard...');
      setTimeout(() => onPaymentComplete?.(updatedUser), 650);
    } catch (error) {
      setStatus('error');
      setMessage(error.message || 'Payment failed. Please retry.');
    }
  };

  if (!selectedPlan) {
    return (
      <section className="checkout-page">
        <div className="checkout-empty">
          <div className="section-label">Checkout</div>
          <h1>No plan selected</h1>
          <p>Please choose an assessment plan before continuing to payment.</p>
          <button className="btn-primary" onClick={onBackToPlans}>Back to Plans <ArrowRight size={15} /></button>
        </div>
      </section>
    );
  }

  return (
    <section className="checkout-page" aria-label="MindVrik checkout">
      <div className="checkout-shell">
        <div className="checkout-main">
          <button className="checkout-back" onClick={onBackToPlans}><ArrowLeft size={15} /> Back to Plans</button>
          <div className="section-label">Secure Checkout</div>
          <h1>{selectedPlan.name}</h1>
          <p className="checkout-lead">
            Review your selected MindVrik assessment plan and complete payment before starting the assessment.
          </p>

          <div className="checkout-user-card">
            <h3>Student / User Details</h3>
            <div className="checkout-detail-grid">
              <span>Student<strong>{user?.studentName || 'Not provided'}</strong></span>
              <span>Parent<strong>{user?.parentName || 'Not provided'}</strong></span>
              <span>Email<strong>{user?.email}</strong></span>
              <span>Class / Grade<strong>{user?.grade || 'Not provided'}</strong></span>
              <span>City<strong>{user?.city || 'Not provided'}</strong></span>
              <span>Mobile<strong>{user?.mobile || 'Not provided'}</strong></span>
            </div>
          </div>

          <div className="checkout-payment-box">
            <div>
              <CreditCard size={20} />
              <h3>Payment Method</h3>
              <p>Razorpay Checkout will open securely. Assessment access unlocks only after backend verification.</p>
            </div>
            <div className="mock-payment-note">Razorpay</div>
          </div>

          {message && <div className={`checkout-message ${status}`}>{message}</div>}

          <button className="btn-primary checkout-pay" onClick={payNow} disabled={status === 'loading' || status === 'success'}>
            {status === 'loading' ? 'Processing...' : `Pay Now ${selectedPlan.price}`} <ArrowRight size={15} />
          </button>
        </div>

        <aside className="checkout-summary">
          <div className="section-label">Order Summary</div>
          <h2>{selectedPlan.price}</h2>
          <p>{selectedPlan.name}</p>
          <div className="checkout-divider" />
          <div className="checkout-feature-list">
            {selectedPlan.features.map((feature) => (
              <span key={feature}><CheckCircle size={14} />{feature}</span>
            ))}
          </div>
          <div className="checkout-secure">
            <ShieldCheck size={18} />
            <span>Payment unlocks assessment access after server-side signature verification.</span>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default CheckoutPage;
